/**
 * Labels for the things the API returns as stored enums.
 *
 * The server hands back 'runner_up', not 'Runner-up', and export-results.js
 * explains why it must: a prettified value matches nothing in
 * placement_points_config, so the raw enum travels and the client names it. This
 * is where it gets named, once, rather than in each of the four Stage 4 pages
 * that show a placement.
 */

/**
 * Placements, worst to best, as completion.js writes them: ROUND_PLACEMENT maps
 * the round a player lost in, plus 'winner' for the one who did not and 'group'
 * for everyone knocked out before the bracket. Null means they never threw —
 * completeTournament leaves the column unset rather than inventing a placing for
 * somebody who was entered and absent.
 */
const PLACEMENT_LABELS = {
  winner:    'Winner',
  runner_up: 'Runner-up',
  semi:      'Semi-finalist',
  quarter:   'Quarter-finalist',
  r16:       'Last 16',
  r32:       'Last 32',
  group:     'Group stage',
};

export function placementLabel(placement) {
  if (!placement) return '—';
  // An unknown value is shown as itself rather than swallowed: a placement this
  // file has not been taught is a fact about the night, and hiding it would make
  // a new enum look like missing data.
  return PLACEMENT_LABELS[placement] ?? placement;
}

/**
 * Gold for the win, quiet for everything else.
 *
 * Silver and bronze exist as tokens (--hd-medal-silver, --hd-medal-bronze) and
 * are deliberately unused here: third place is not a placement this tournament
 * format produces — the losing semi-finalists are both 'semi', and nothing plays
 * off between them.
 */
export function placementBadgeClass(placement) {
  if (placement === 'winner')    return 'hd-badge hd-badge-gold';
  if (placement === 'runner_up') return 'hd-badge hd-badge-accent';
  return 'hd-badge hd-badge-neutral';
}

/** 1 -> '1st'. For a rank read aloud as "third of twelve". */
export function ordinal(n) {
  if (typeof n !== 'number') return '—';

  const mod100 = n % 100;
  // 11th, 12th, 13th break the units-digit rule and are the reason this is not
  // a three-case lookup.
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;

  const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th';
  return `${n}${suffix}`;
}

/** 'Night 4', or the date alone when a tournament carries no number. */
export function tournamentLabel(nightNumber) {
  return nightNumber != null ? `Night ${nightNumber}` : 'Tournament';
}

/**
 * What a match was: where it sat, and how long it ran.
 *
 * 'Group 2 · Best of 3' or 'Quarter-Final · Best of 5'. roundName arrives from
 * the server — lib/knockout.js owns that mapping, and re-deriving it here from
 * the round number would put the bracket's vocabulary in two places.
 */
export function matchFormatLabel(match) {
  const where = match.stage === 'knockout'
    ? (match.roundName ?? 'Knockout')
    : (match.groupNumber != null ? `Group ${match.groupNumber}` : 'Group stage');

  return match.bestOfLegs != null
    ? `${where} · Best of ${match.bestOfLegs}`
    : where;
}

/**
 * Matches split into the headings a night is read under: the group stage, then
 * each knockout round.
 *
 * ORDER IS THE SERVER'S. The rows arrive in MATCH_PLAY_ORDER_SQL order — group
 * first, then knockout rounds counting down to the final — so grouping is a
 * single pass that keeps whatever order it was handed. Sorting here would be a
 * second opinion about the running order, and entry-names.js documents at length
 * why there is only one.
 */
export function groupMatchesByStage(matches) {
  const sections = [];
  let current = null;

  for (const match of matches) {
    const title = match.stage === 'knockout'
      ? (match.roundName ?? 'Knockout')
      : 'Group Stage';

    if (!current || current.title !== title) {
      current = { title, matches: [] };
      sections.push(current);
    }

    current.matches.push(match);
  }

  return sections;
}
